/**
 * RegionPicker — searchable tree-view dropdown for Ukrainian regions,
 * backed by the admin-only /admin/alerts/regions endpoint. Used by the
 * alerts history filter sidebar and the simulation page.
 *
 * Upstream regions are hierarchical (State → District → Community →
 * CityOrVillage/CityDistrict). Rendering 1500 rows as a flat list is
 * overwhelming, so the picker:
 *   - fetches the full tree once per admin session (1h stale / 24h gc)
 *   - renders only the top-level states by default, with a ▶/▼ chevron
 *     for any region that has children
 *   - clicking the chevron toggles the subtree; clicking the row body
 *     selects that region and closes the popover
 *   - searching auto-expands every ancestor of every match so results
 *     stay anchored in context (matches shown in situ, not as a flat
 *     "search result" list)
 *   - also auto-expands the ancestor chain of the currently selected
 *     region on open, so reopening the picker lands the user where
 *     they left off
 *
 * Render contract:
 *   - Controlled by `value` (regionId string) + `onChange(regionId, region)`.
 *   - `onChange` passes the full region object so callers can cache the
 *     display name without a second lookup (e.g. the simulation confirm
 *     modal needs it for type-to-confirm).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAdminRegions } from '../hooks/useAdminAlerts';
import type { UkraineRegionDto } from '../api/adminAlertsApi';

export interface RegionPickerProps {
  value: string;
  onChange: (regionId: string, region: UkraineRegionDto | null) => void;
  /** Optional placeholder when nothing is selected. Defaults to "Any region". */
  placeholder?: string;
  /** Allow clearing the selection. Defaults to true. */
  clearable?: boolean;
  size?: 'sm' | 'md';
  'data-testid'?: string;
}

export function RegionPicker({
  value,
  onChange,
  placeholder,
  clearable = true,
  size = 'sm',
  'data-testid': testId = 'region-picker',
}: RegionPickerProps) {
  const { t } = useTranslation();
  const { data: regions = [], isLoading } = useAdminRegions();
  const [search, setSearch] = useState('');
  const [userExpanded, setUserExpanded] = useState<Set<string>>(() => new Set());
  const { isOpen, onOpen, onClose } = useDisclosure();
  const containerRef = useRef<HTMLDivElement>(null);

  useOutsideClick({ ref: containerRef, handler: () => onClose() });

  // Build a parent→children index + depth/path helpers in one pass.
  const regionIndex = useMemo(() => {
    const byId = new Map<string, UkraineRegionDto>();
    const childrenByParent = new Map<string | null, UkraineRegionDto[]>();
    for (const r of regions) {
      byId.set(r.regionId, r);
      const key = r.parentRegionId ?? null;
      const bucket = childrenByParent.get(key);
      if (bucket) bucket.push(r);
      else childrenByParent.set(key, [r]);
    }
    // Sort each bucket alphabetically for stable presentation.
    for (const [, bucket] of childrenByParent) {
      bucket.sort((a, b) => a.regionName.localeCompare(b.regionName));
    }

    const hasChildren = (id: string) => (childrenByParent.get(id)?.length ?? 0) > 0;

    const depthOf = (id: string): number => {
      let d = 0;
      let cursor = byId.get(id);
      const seen = new Set<string>();
      while (cursor?.parentRegionId && !seen.has(cursor.regionId)) {
        seen.add(cursor.regionId);
        cursor = byId.get(cursor.parentRegionId);
        d++;
      }
      return d;
    };

    const ancestorsOf = (id: string): string[] => {
      const acc: string[] = [];
      let cursor = byId.get(id);
      const seen = new Set<string>();
      while (cursor?.parentRegionId && !seen.has(cursor.regionId)) {
        seen.add(cursor.regionId);
        cursor = byId.get(cursor.parentRegionId);
        if (cursor) acc.unshift(cursor.regionId);
      }
      return acc;
    };

    const pathNamesOf = (id: string): string[] =>
      ancestorsOf(id)
        .map((aid) => byId.get(aid)?.regionName)
        .filter((n): n is string => Boolean(n));

    const roots = childrenByParent.get(null) ?? [];

    return { byId, childrenByParent, hasChildren, depthOf, ancestorsOf, pathNamesOf, roots };
  }, [regions]);

  // When opening the popover with a pre-selected value, auto-expand the
  // ancestor chain so the selection is visible without scrolling.
  useEffect(() => {
    if (!isOpen || !value) return;
    const ancestors = regionIndex.ancestorsOf(value);
    if (ancestors.length === 0) return;
    setUserExpanded((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const a of ancestors) {
        if (!next.has(a)) {
          next.add(a);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [isOpen, value, regionIndex]);

  // Compute matching rows (search) and the effective-expansion set.
  // In search mode we implicitly expand every ancestor of every match so
  // results show in context without the user clicking through manually.
  const { visibleRows, matchedIds, effectiveExpanded } = useMemo(() => {
    const needle = search.trim().toLowerCase();

    if (needle === '') {
      // Tree mode: BFS from roots, drilling into userExpanded nodes.
      const rows: UkraineRegionDto[] = [];
      const walk = (parentId: string | null) => {
        const bucket = regionIndex.childrenByParent.get(parentId) ?? [];
        for (const r of bucket) {
          rows.push(r);
          if (userExpanded.has(r.regionId)) walk(r.regionId);
        }
      };
      walk(null);
      return { visibleRows: rows, matchedIds: new Set<string>(), effectiveExpanded: userExpanded };
    }

    // Search mode: find matches, then expand all their ancestors.
    const matched = new Set<string>();
    for (const r of regions) {
      const hay = [
        r.regionName,
        r.regionEngName ?? '',
        r.regionId,
        ...regionIndex.pathNamesOf(r.regionId),
      ]
        .join(' ')
        .toLowerCase();
      if (hay.includes(needle)) matched.add(r.regionId);
    }

    const expandedNow = new Set(userExpanded);
    for (const id of matched) {
      for (const a of regionIndex.ancestorsOf(id)) expandedNow.add(a);
    }

    // Show every match PLUS the ancestors (so results are readable in
    // context) PLUS the children of any match the user also expanded.
    const visibleSet = new Set<string>(matched);
    for (const id of matched) {
      for (const a of regionIndex.ancestorsOf(id)) visibleSet.add(a);
    }

    const rows: UkraineRegionDto[] = [];
    const walk = (parentId: string | null) => {
      const bucket = regionIndex.childrenByParent.get(parentId) ?? [];
      for (const r of bucket) {
        if (!visibleSet.has(r.regionId)) continue;
        rows.push(r);
        if (expandedNow.has(r.regionId)) walk(r.regionId);
      }
    };
    walk(null);

    return { visibleRows: rows, matchedIds: matched, effectiveExpanded: expandedNow };
  }, [regions, regionIndex, userExpanded, search]);

  const selected = useMemo(
    () => regions.find((r) => r.regionId === value) ?? null,
    [regions, value],
  );
  const selectedPath = selected ? regionIndex.pathNamesOf(selected.regionId) : [];

  const displayLabel = selected
    ? selectedPath.length > 0
      ? `${selected.regionName} · ${selectedPath.join(' › ')}`
      : selected.regionName
    : placeholder ?? t('admin.alerts.regionPicker.anyRegion', 'Any region');

  const handleSelect = (region: UkraineRegionDto) => {
    onChange(region.regionId, region);
    setSearch('');
    onClose();
  };

  const handleClear = () => {
    onChange('', null);
    setSearch('');
  };

  const handleToggleExpand = (id: string) => {
    setUserExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box ref={containerRef} data-testid={testId}>
      <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start" matchWidth autoFocus={false}>
        <PopoverTrigger>
          <Flex
            as="button"
            type="button"
            align="center"
            justify="space-between"
            w="100%"
            px={3}
            h={size === 'md' ? '40px' : '32px'}
            borderRadius="10px"
            borderWidth="1px"
            borderColor={isOpen ? 'brand.500' : 'gray.200'}
            bg="white"
            fontSize="sm"
            textAlign="left"
            transition="border-color 0.15s"
            _hover={{ borderColor: 'gray.300' }}
            onClick={() => (isOpen ? onClose() : onOpen())}
            data-testid={`${testId}-trigger`}
          >
            <Text
              color={selected ? 'gray.700' : 'gray.400'}
              noOfLines={1}
              fontWeight={selected ? '500' : '400'}
            >
              {displayLabel}
            </Text>
            <Flex align="center" gap={1} ms={2}>
              {clearable && selected && (
                <Box
                  as="span"
                  fontSize="xs"
                  color="gray.400"
                  _hover={{ color: 'gray.600' }}
                  onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                  aria-label={t('common.clear', 'Clear')}
                  data-testid={`${testId}-clear`}
                >
                  ×
                </Box>
              )}
              <Text fontSize="xs" color="gray.400">
                ▾
              </Text>
            </Flex>
          </Flex>
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            w="var(--popper-reference-width)"
            maxH="360px"
            overflow="hidden"
            borderRadius="14px"
            boxShadow="0px 18px 40px rgba(112, 144, 176, 0.2)"
          >
            <PopoverBody p={2}>
              <InputGroup size="sm" mb={2}>
                <Input
                  placeholder={t('admin.alerts.regionPicker.searchPlaceholder', 'Search by name or ID…')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                  borderRadius="8px"
                  data-testid={`${testId}-search`}
                />
                {isLoading && (
                  <InputRightElement>
                    <Spinner size="xs" color="gray.400" />
                  </InputRightElement>
                )}
              </InputGroup>

              <VStack align="stretch" spacing={0} maxH="280px" overflowY="auto">
                {visibleRows.length === 0 && !isLoading && (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py={3}>
                    {t('admin.alerts.regionPicker.empty', 'No regions match.')}
                  </Text>
                )}
                {visibleRows.map((region) => {
                  const active = region.regionId === value;
                  const depth = regionIndex.depthOf(region.regionId);
                  const hasChildren = regionIndex.hasChildren(region.regionId);
                  const expanded = effectiveExpanded.has(region.regionId);
                  const isMatch = matchedIds.has(region.regionId);
                  return (
                    <Flex
                      key={region.regionId}
                      align="center"
                      px={2}
                      py={1.5}
                      pl={2 + depth * 4}
                      borderRadius="6px"
                      cursor="pointer"
                      bg={active ? 'brand.50' : 'transparent'}
                      _hover={{ bg: active ? 'brand.100' : 'gray.50' }}
                      onClick={() => handleSelect(region)}
                      data-testid={`${testId}-option-${region.regionId}`}
                    >
                      {/* Expand / collapse chevron. 16px-wide well even when the row
                          has no children, so labels align across siblings. */}
                      <Box
                        w="20px"
                        h="20px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        cursor={hasChildren ? 'pointer' : 'default'}
                        color="gray.400"
                        _hover={hasChildren ? { color: 'gray.700' } : undefined}
                        onClick={
                          hasChildren
                            ? (e: React.MouseEvent<HTMLDivElement>) => {
                                e.stopPropagation();
                                handleToggleExpand(region.regionId);
                              }
                            : undefined
                        }
                        aria-label={
                          hasChildren
                            ? expanded
                              ? t('admin.alerts.regionPicker.collapse', 'Collapse')
                              : t('admin.alerts.regionPicker.expand', 'Expand')
                            : undefined
                        }
                        data-testid={
                          hasChildren
                            ? `${testId}-toggle-${region.regionId}`
                            : undefined
                        }
                      >
                        {hasChildren ? (
                          <Text fontSize="xs" fontWeight="bold" lineHeight={1}>
                            {expanded ? '▼' : '▶'}
                          </Text>
                        ) : null}
                      </Box>

                      <Flex direction="column" minW={0} flex={1} ms={1}>
                        <Text
                          fontSize="sm"
                          color={active ? 'brand.700' : 'gray.700'}
                          fontWeight={active ? '600' : isMatch ? '600' : '500'}
                          noOfLines={1}
                        >
                          {region.regionName}
                        </Text>
                        {region.regionEngName &&
                          region.regionEngName !== region.regionName && (
                            <Text fontSize="xs" color="gray.500" noOfLines={1}>
                              {region.regionEngName}
                            </Text>
                          )}
                      </Flex>
                      <Flex direction="column" align="flex-end" ms={2} flexShrink={0}>
                        <Text fontSize="2xs" color="gray.400" textTransform="uppercase">
                          {region.regionType}
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                          #{region.regionId}
                        </Text>
                      </Flex>
                    </Flex>
                  );
                })}
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </Box>
  );
}
